/**
 * ParticipantTypesSection
 *
 * SCRUM-1292: GUIDE-008 Participant Types UI
 *
 * Guide-facing section for managing participant type discounts.
 * Includes mandatory legal disclosure text (AC-02), CRUD operations,
 * and rate type restricted to multiplier/free only (AC-05).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Plus, Pencil, Trash2, AlertCircle, Users } from 'lucide-react';
import {
  getParticipantTypes,
  createParticipantType,
  updateParticipantType,
  deactivateParticipantType
} from '../../../services/guidePricingApi';

const LEGAL_DISCLOSURE_TEXT =
  'Age-based discounts are common in outdoor recreation and are generally permissible. ' +
  'Pricing surcharges based on age may be subject to legal restrictions in some states, ' +
  'including California. You are responsible for compliance in your operating jurisdiction. ' +
  'OutdoorGuides currently supports age-based discounts only — pricing surcharges based on ' +
  'participant age are not permitted on the platform.';

/**
 * Format discount for display.
 * rate_value = 0.85 → "15% off"
 * rate_type = 'free' → "100% off (Free)"
 */
function formatDiscount(type) {
  if (type.rate_type === 'free') {
    return '100% off (Free)';
  }
  const discountPct = Math.round((1 - parseFloat(type.rate_value)) * 100);
  return `${discountPct}% off`;
}

/**
 * Format age range for display.
 */
function formatAgeRange(type) {
  if (type.min_age !== null && type.min_age !== undefined &&
      type.max_age !== null && type.max_age !== undefined) {
    return `${type.min_age}–${type.max_age} years`;
  }
  return 'Any age';
}

/**
 * Participant type form for add/edit.
 */
function ParticipantTypeForm({ initialData, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    name: initialData?.name || '',
    code: initialData?.code || '',
    description: initialData?.description || '',
    rateType: initialData?.rate_type || 'multiplier',
    discountPercent: initialData?.rate_type === 'free'
      ? ''
      : initialData?.rate_value !== undefined && initialData?.rate_value !== null
        ? String(Math.round((1 - parseFloat(initialData.rate_value)) * 100))
        : '',
    minAge: initialData?.min_age !== null && initialData?.min_age !== undefined
      ? String(initialData.min_age) : '',
    maxAge: initialData?.max_age !== null && initialData?.max_age !== undefined
      ? String(initialData.max_age) : '',
  });
  const [error, setError] = useState(null);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    // Validate
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!form.code.trim()) {
      setError('Code is required');
      return;
    }

    // Age range validation
    const hasMinAge = form.minAge !== '';
    const hasMaxAge = form.maxAge !== '';
    if (hasMinAge !== hasMaxAge) {
      setError('Both min age and max age must be provided, or neither');
      return;
    }
    if (hasMinAge && hasMaxAge) {
      const minAge = parseInt(form.minAge, 10);
      const maxAge = parseInt(form.maxAge, 10);
      if (isNaN(minAge) || isNaN(maxAge)) {
        setError('Age values must be numbers');
        return;
      }
      if (minAge < 0) {
        setError('Min age must be 0 or greater');
        return;
      }
      if (minAge > maxAge) {
        setError('Min age must be less than or equal to max age');
        return;
      }
    }

    // Rate validation (AC-06)
    let rateValue;
    if (form.rateType === 'free') {
      rateValue = 0;
    } else {
      const discountPct = parseFloat(form.discountPercent);
      if (isNaN(discountPct) || discountPct < 0) {
        setError('Discount percentage must be 0 or greater');
        return;
      }
      if (discountPct > 100) {
        setError('Discount cannot exceed 100%');
        return;
      }
      rateValue = Math.round((1 - discountPct / 100) * 10000) / 10000;
      if (rateValue > 1.0) {
        setError('Rate value cannot exceed 1.0 — surcharges are not permitted');
        return;
      }
    }

    const data = {
      name: form.name.trim(),
      code: form.code.trim(),
      description: form.description.trim() || undefined,
      rateType: form.rateType,
      rateValue,
      minAge: form.minAge !== '' ? parseInt(form.minAge, 10) : undefined,
      maxAge: form.maxAge !== '' ? parseInt(form.maxAge, 10) : undefined,
    };

    onSave(data);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-4 space-y-4 border border-gray-200">
      <h4 className="font-medium text-gray-900">
        {initialData ? 'Edit Participant Type' : 'Add Participant Type'}
      </h4>

      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded" role="alert">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g. Child"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
          <input
            type="text"
            value={form.code}
            onChange={(e) => handleChange('code', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
            placeholder="e.g. child"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
        <input
          type="text"
          value={form.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="e.g. Ages 3-12"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Min Age (optional)</label>
          <input
            type="number"
            value={form.minAge}
            onChange={(e) => handleChange('minAge', e.target.value)}
            min="0"
            max="120"
            placeholder="e.g. 3"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Max Age (optional)</label>
          <input
            type="number"
            value={form.maxAge}
            onChange={(e) => handleChange('maxAge', e.target.value)}
            min="0"
            max="120"
            placeholder="e.g. 12"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
      </div>

      {/* AC-05: Rate type select — ONLY multiplier and free */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Rate Type *</label>
        <select
          value={form.rateType}
          onChange={(e) => handleChange('rateType', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          data-testid="rate-type-select"
        >
          <option value="multiplier">Discount (percentage off)</option>
          <option value="free">Free (100% off)</option>
        </select>
      </div>

      {/* Discount value — only shown when rate_type = 'multiplier' */}
      {form.rateType === 'multiplier' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Discount Percentage *</label>
          <div className="relative">
            <input
              type="number"
              value={form.discountPercent}
              onChange={(e) => handleChange('discountPercent', e.target.value)}
              min="0"
              max="100"
              step="1"
              placeholder="e.g. 15 for 15% off"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 pr-8"
              data-testid="discount-percent-input"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Enter the discount percentage. E.g., 15 means 15% off the standard price.
          </p>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 text-sm font-medium"
        >
          {saving ? 'Saving...' : initialData ? 'Update' : 'Add'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * ParticipantTypesSection
 */
export default function ParticipantTypesSection({ experienceId }) {
  const [participantTypes, setParticipantTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadTypes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getParticipantTypes(experienceId);
      setParticipantTypes(res.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load participant types');
    } finally {
      setLoading(false);
    }
  }, [experienceId]);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  const handleCreate = async (data) => {
    try {
      setSaving(true);
      setError(null);
      await createParticipantType(experienceId, data);
      setShowForm(false);
      await loadTypes();
    } catch (err) {
      const message = err.error?.message || err.message || 'Failed to create participant type';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (data) => {
    if (!editingType) {
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await updateParticipantType(experienceId, editingType.id, data);
      setEditingType(null);
      await loadTypes();
    } catch (err) {
      const message = err.error?.message || err.message || 'Failed to update participant type';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (typeId) => {
    try {
      setSaving(true);
      setError(null);
      await deactivateParticipantType(experienceId, typeId);
      await loadTypes();
    } catch (err) {
      const message = err.error?.message || err.message || 'Failed to deactivate participant type';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-white rounded-lg shadow p-6" data-testid="participant-types-section">
      <div className="flex items-center gap-2 mb-4">
        <Users className="h-5 w-5 text-emerald-600" />
        <h2 className="text-lg font-bold text-gray-900">Participant Types</h2>
      </div>

      {/* AC-02: Legal disclosure — VERBATIM, rendered before any Add button */}
      <div
        className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 text-sm text-amber-800"
        role="note"
        data-testid="legal-disclosure"
      >
        {LEGAL_DISCLOSURE_TEXT}
      </div>

      {/* Error display */}
      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded mb-4" role="alert">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <>
          {/* AC-03: List of configured participant types */}
          {participantTypes.length > 0 && (
            <div className="mb-4 space-y-2" data-testid="participant-types-list">
              {participantTypes.map((type) => (
                <div
                  key={type.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                  data-testid={`participant-type-${type.id}`}
                >
                  <div>
                    <span className="font-medium text-gray-900">{type.name}</span>
                    <span className="text-gray-500 text-sm ml-2">({type.code})</span>
                    <div className="text-sm text-gray-600 mt-0.5">
                      <span>{formatAgeRange(type)}</span>
                      <span className="mx-2">·</span>
                      <span className="text-emerald-600 font-medium">{formatDiscount(type)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingType(type);
                        setShowForm(false);
                      }}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                      title="Edit"
                      data-testid={`edit-type-${type.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeactivate(type.id)}
                      disabled={saving}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                      title="Deactivate"
                      data-testid={`deactivate-type-${type.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {participantTypes.length === 0 && !showForm && !editingType && (
            <p className="text-sm text-gray-500 mb-4">
              No participant types configured. Add types to offer age-based discounts.
            </p>
          )}

          {/* AC-04: Add form */}
          {showForm && (
            <ParticipantTypeForm
              onSave={handleCreate}
              onCancel={() => setShowForm(false)}
              saving={saving}
            />
          )}

          {/* AC-07: Edit form */}
          {editingType && (
            <ParticipantTypeForm
              initialData={editingType}
              onSave={handleUpdate}
              onCancel={() => setEditingType(null)}
              saving={saving}
            />
          )}

          {/* Add button — below legal disclosure (AC-02) */}
          {!showForm && !editingType && (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-emerald-600 border border-emerald-300 rounded-lg hover:bg-emerald-50"
              data-testid="add-participant-type-btn"
            >
              <Plus className="h-4 w-4" />
              Add Participant Type
            </button>
          )}
        </>
      )}
    </section>
  );
}
